import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Boxes, Plus, Trash2 } from "lucide-react";

const statusCls = { DRAFT: "border-slate-200 bg-slate-50 text-slate-600", PROCESSING: "border-amber-200 bg-amber-50 text-amber-700", CONFIRMED: "border-emerald-200 bg-emerald-50 text-emerald-700", CANCELLED: "border-rose-200 bg-rose-50 text-rose-600" };

export default function DpMaterial() {
  const { can } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [issues, setIssues] = useState(null);
  const [summary, setSummary] = useState([]);
  const [items, setItems] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [draft, setDraft] = useState(null);
  const [act, setAct] = useState(null); // {kind, line}

  useEffect(() => {
    api.get("/digital-printing/production/queue").then((r) => setJobs(r.data)).catch(() => {});
    api.get("/digital-printing/material/items").then((r) => setItems(r.data)).catch(() => {});
    api.get("/digital-printing/material/meta").then((r) => setReasons(r.data.waste_reasons || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!jobId) { setIssues(null); setSummary([]); return; }
    api.get(`/digital-printing/jobs/${jobId}/material-issues`).then((r) => setIssues(r.data)).catch(() => setIssues([]));
    api.get(`/digital-printing/jobs/${jobId}/material-summary`).then((r) => setSummary(r.data.items)).catch(() => setSummary([]));
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const newDraft = () => setDraft({ lines: [{ inventory_item_id: "", issue_qty: 1, usage_or_pack_unit: "", usage_tracking_mode: "MEASURED", notes: "" }], notes: "" });
  const saveDraft = async () => {
    try { await api.post(`/digital-printing/jobs/${jobId}/material-issues`, draft); toast.success("Draft Material Issue dibuat"); setDraft(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const confirm = async (id) => { try { await api.post(`/digital-printing/material-issues/${id}/confirm`); toast.success("Material Issue CONFIRMED — stok berkurang"); load(); } catch (e) { toast.error(apiError(e)); } };
  const cancelDraft = async (id) => { try { await api.post(`/digital-printing/material-issues/${id}/cancel`); toast.success("Draft dibatalkan"); load(); } catch (e) { toast.error(apiError(e)); } };

  const doAct = async (payload) => {
    const ep = { consume: "material-consumptions", waste: "material-waste", return: "material-returns" }[act.kind];
    try { await api.post(`/digital-printing/jobs/${jobId}/${ep}`, payload); toast.success("Tercatat"); setAct(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="dp-material">
      <PageHeader title="Material Digital Printing" subtitle="Material Issue / Consumption / Waste / Return. Stok turun HANYA saat Confirm Issue. Tanpa costing/rupiah." />

      <Card className="mb-4 p-3">
        <label className="text-xs font-medium text-slate-600">Pilih Job Produksi Aktif
          <select value={jobId} onChange={(e) => setJobId(e.target.value)} className="mt-1 block w-full rounded border px-2 py-2 text-sm md:w-1/2" data-testid="dp-mat-job">
            <option value="">- pilih job (QUEUED/IN_PROGRESS/QC) -</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_number} · {j.service_snapshot?.service_name} · {j.status}</option>)}
          </select>
        </label>
      </Card>

      {!jobId ? (
        <EmptyState title="Pilih Job" subtitle="Material Issue hanya untuk Job yang sudah masuk produksi." icon={Boxes} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">Material Issues</h3>
            {can("digital_printing.material.request") && <Button size="sm" onClick={newDraft} data-testid="dp-mat-new" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1 h-4 w-4" />Draft Issue</Button>}
          </div>

          {issues == null ? <Loading /> : issues.length === 0 ? <p className="text-xs text-slate-400">Belum ada Material Issue.</p> : issues.map((iss) => (
            <Card key={iss.id} className="p-3" data-testid={`dp-mat-issue-${iss.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><span className="font-mono text-xs">{iss.issue_number}</span><Badge variant="outline" className={statusCls[iss.status]}>{iss.status}</Badge>{iss.shortage && <Badge variant="outline" className="border-rose-200 bg-rose-50 text-rose-700">MATERIAL_SHORTAGE</Badge>}</div>
                {iss.status === "DRAFT" && (
                  <div className="flex gap-1">
                    {can("digital_printing.material.issue") && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-500" onClick={() => confirm(iss.id)} data-testid={`dp-mat-confirm-${iss.id}`}>Confirm</Button>}
                    {can("digital_printing.material.request") && <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => cancelDraft(iss.id)} data-testid={`dp-mat-cancel-${iss.id}`}>Batal</Button>}
                  </div>
                )}
              </div>
              <table className="mt-2 w-full text-xs">
                <thead><tr className="text-left text-slate-400"><th>Item</th><th className="text-right">Issue Qty</th><th>Unit</th><th>Mode</th><th className="text-right">Stok -/(unit)</th><th></th></tr></thead>
                <tbody>
                  {iss.lines.map((ln) => (
                    <tr key={ln.id} className="border-t">
                      <td className="py-1">{ln.item_name}</td>
                      <td className="text-right">{ln.issue_qty}</td>
                      <td>{ln.usage_or_pack_unit}</td>
                      <td>{ln.usage_tracking_mode === "ISSUE_ONLY" ? "Issue Only" : "Measured"}</td>
                      <td className="text-right text-slate-400">{ln.base_qty} {ln.base_stock_unit}</td>
                      <td className="text-right">
                        {iss.status === "CONFIRMED" && (
                          <span className="flex justify-end gap-1">
                            {ln.usage_tracking_mode === "MEASURED" && can("digital_printing.material.consume") && <Button size="sm" variant="ghost" onClick={() => setAct({ kind: "consume", line: ln })} data-testid={`dp-mat-consume-${ln.id}`}>Consume</Button>}
                            {can("digital_printing.material.waste") && <Button size="sm" variant="ghost" className="text-amber-600" onClick={() => setAct({ kind: "waste", line: ln })} data-testid={`dp-mat-waste-${ln.id}`}>Waste</Button>}
                            {can("digital_printing.material.return") && <Button size="sm" variant="ghost" className="text-sky-600" onClick={() => setAct({ kind: "return", line: ln })} data-testid={`dp-mat-return-${ln.id}`}>Return</Button>}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ))}

          <Card className="p-3" data-testid="dp-mat-summary">
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Material Summary (per item/unit)</h3>
            {summary.length === 0 ? <p className="text-xs text-slate-400">Belum ada material confirmed.</p> : (
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-400"><th>Item</th><th className="text-right">Issued</th><th className="text-right">Consumed</th><th className="text-right">Waste</th><th className="text-right">Returned</th><th className="text-right">Remaining</th><th>Mode</th></tr></thead>
                <tbody>
                  {summary.map((s, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-1">{s.item_name}</td>
                      <td className="text-right">{s.issued} {s.usage_unit}</td>
                      <td className="text-right">{s.tracking_mode === "ISSUE_ONLY" ? "—" : s.consumed}</td>
                      <td className="text-right">{s.tracking_mode === "ISSUE_ONLY" ? "—" : s.waste}</td>
                      <td className="text-right">{s.returned}</td>
                      <td className="text-right font-semibold">{s.tracking_mode === "ISSUE_ONLY" ? "Issue Only" : s.remaining}</td>
                      <td>{s.tracking_mode === "ISSUE_ONLY" ? "Detail tidak diukur" : "Measured"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      )}

      {/* Draft issue dialog */}
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-2xl" data-testid="dp-mat-draft-dialog">
          <DialogHeader><DialogTitle>Draft Material Issue</DialogTitle><DialogDescription>Tambahkan baris material. Stok hanya berkurang saat Confirm.</DialogDescription></DialogHeader>
          {draft && (
            <div className="space-y-2">
              {draft.lines.map((ln, i) => {
                const it = items.find((x) => x.id === ln.inventory_item_id);
                return (
                  <div key={i} className="grid grid-cols-12 gap-2">
                    <select className="col-span-4 rounded border px-2 py-1.5 text-xs" value={ln.inventory_item_id} data-testid={`dp-mat-line-item-${i}`}
                      onChange={(e) => { const it2 = items.find((x) => x.id === e.target.value); setDraft({ ...draft, lines: draft.lines.map((x, j) => j === i ? { ...x, inventory_item_id: e.target.value, usage_or_pack_unit: it2?.unit || "" } : x) }); }}>
                      <option value="">- item -</option>
                      {items.map((x) => <option key={x.id} value={x.id}>{x.name} (avail {x.available} {x.unit})</option>)}
                    </select>
                    <Input className="col-span-2" type="number" min="0" value={ln.issue_qty} data-testid={`dp-mat-line-qty-${i}`} onChange={(e) => setDraft({ ...draft, lines: draft.lines.map((x, j) => j === i ? { ...x, issue_qty: e.target.value } : x) })} />
                    <select className="col-span-2 rounded border px-2 py-1.5 text-xs" value={ln.usage_or_pack_unit} data-testid={`dp-mat-line-unit-${i}`} onChange={(e) => setDraft({ ...draft, lines: draft.lines.map((x, j) => j === i ? { ...x, usage_or_pack_unit: e.target.value } : x) })}>
                      {(it?.usage_units || [ln.usage_or_pack_unit]).filter(Boolean).map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <select className="col-span-3 rounded border px-2 py-1.5 text-xs" value={ln.usage_tracking_mode} data-testid={`dp-mat-line-mode-${i}`} onChange={(e) => setDraft({ ...draft, lines: draft.lines.map((x, j) => j === i ? { ...x, usage_tracking_mode: e.target.value } : x) })}>
                      <option value="MEASURED">Measured</option><option value="ISSUE_ONLY">Issue Only</option>
                    </select>
                    <button className="col-span-1 text-rose-500" onClick={() => setDraft({ ...draft, lines: draft.lines.filter((_, j) => j !== i) })}><Trash2 className="h-4 w-4" /></button>
                  </div>
                );
              })}
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, lines: [...draft.lines, { inventory_item_id: "", issue_qty: 1, usage_or_pack_unit: "", usage_tracking_mode: "MEASURED", notes: "" }] })} data-testid="dp-mat-add-line"><Plus className="mr-1 h-3.5 w-3.5" />Line</Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDraft(null)}>Batal</Button>
            <Button onClick={saveDraft} data-testid="dp-mat-draft-save" className="bg-emerald-600 hover:bg-emerald-500">Simpan Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Consume / Waste / Return dialog */}
      <Dialog open={!!act} onOpenChange={(o) => !o && setAct(null)}>
        <DialogContent data-testid="dp-mat-act-dialog">
          <DialogHeader><DialogTitle>{act?.kind === "consume" ? "Catat Consumption" : act?.kind === "waste" ? "Catat Waste" : "Material Return"} — {act?.line?.item_name}</DialogTitle><DialogDescription>Qty dalam satuan pemakaian ({act?.line?.usage_or_pack_unit}).</DialogDescription></DialogHeader>
          {act && <ActForm act={act} reasons={reasons} onSubmit={doAct} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ActForm({ act, reasons, onSubmit }) {
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const submit = () => {
    const base = { issue_line_id: act.line.id, qty: Number(qty), notes };
    if (act.kind === "waste") onSubmit({ ...base, reason });
    else if (act.kind === "return") onSubmit({ ...base, reason });
    else onSubmit(base);
  };
  return (
    <div className="space-y-3">
      <label className="block text-xs">Qty ({act.line.usage_or_pack_unit})<Input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="dp-mat-act-qty" /></label>
      {act.kind === "waste" && (
        <label className="block text-xs">Alasan Waste<select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 block w-full rounded border px-2 py-2 text-sm" data-testid="dp-mat-act-reason"><option value="">- pilih -</option>{reasons.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}</select></label>
      )}
      {act.kind === "return" && <label className="block text-xs">Alasan (opsional)<Input value={reason} onChange={(e) => setReason(e.target.value)} /></label>}
      <label className="block text-xs">Catatan<Input value={notes} onChange={(e) => setNotes(e.target.value)} /></label>
      <Button onClick={submit} data-testid="dp-mat-act-submit" className="w-full bg-emerald-600 hover:bg-emerald-500">Simpan</Button>
    </div>
  );
}
